"use client";

import clsx from "clsx";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "./ui";

/** How a question is asked: a destructive action gets a red confirm button. */
interface AskOptions {
  danger?: boolean;
}

/** The app's own dialogs instead of the browser's (confirm / alert / prompt), each answered through a promise. */
interface Dialogs {
  /** Yes (true) or no (false). */
  confirm: (message: string, options?: AskOptions) => Promise<boolean>;
  /** A note to acknowledge. */
  alert: (message: string) => Promise<void>;
  /** A value to type (null: cancelled). */
  prompt: (message: string, value: string) => Promise<string | null>;
}

type Open = { kind: "confirm" | "alert" | "prompt"; message: string; value: string; danger: boolean; done: (answer: string | null) => void };

const DialogContext = createContext<Dialogs | null>(null);

export function useDialog(): Dialogs {
  const dialogs = useContext(DialogContext);
  if (!dialogs) throw new Error("useDialog outside DialogProvider");
  return dialogs;
}

/** Holds the one open dialog; the components ask through `useDialog()`. */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<Open | null>(null);
  const ask = useCallback(
    (kind: Open["kind"], message: string, value = "", danger = false) =>
      new Promise<string | null>((resolve) => {
        const done = (answer: string | null) => {
          setOpen(null);
          resolve(answer);
        };
        setOpen({ kind, message, value, danger, done });
      }),
    [],
  );
  const [dialogs] = useState<Dialogs>(() => ({
    confirm: async (message, options) => (await ask("confirm", message, "", options?.danger)) !== null,
    alert: async (message) => void (await ask("alert", message)),
    prompt: (message, value) => ask("prompt", message, value),
  }));
  return (
    <DialogContext.Provider value={dialogs}>
      {children}
      {open && <DialogBox key={open.message} open={open} />}
    </DialogContext.Provider>
  );
}

/** How large a box is: a question (420 px), a form (520 px), a whole area (up to 1100 × 88 % of the window, its content scrolling inside). */
type ModalSize = "small" | "wide" | "large";
const MODAL_WIDTH: Record<ModalSize, string> = { small: "max-w-[420px]", wide: "wide:max-w-[520px]", large: "wide:max-w-[1100px]" };
/** A box in the middle, scrolling in itself when it is higher than the window; a whole area almost the window's height. */
const MODAL_SHAPE = {
  box: "max-h-[90dvh] overflow-y-auto rounded-xl p-4",
  large: "flex h-[92dvh] flex-col rounded-xl wide:h-[88dvh]",
} as const;

/** The open boxes, newest last: Escape closes only the topmost (a dialog over a box over the page). */
const openModals: symbol[] = [];

/**
 * A box over the page — in the element shown full screen when there is one (else the browser would hide it), else in
 * the page, in its middle (on phones too); Escape (the topmost box only) or a press beside it closes it. The app's
 * dialogs and its larger forms and areas (the profile, the Telegram setup, the settings) use it; a `large` box's
 * content brings its own padding and scrolling.
 */
export function Modal({ label, role = "dialog", onClose, size = "small", children }: { label: string; role?: "dialog" | "alertdialog"; onClose: () => void; size?: ModalSize; children: ReactNode }) {
  // registered once, while open (its place among the open boxes stays); the latest `onClose` answers
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const me = Symbol("modal");
    openModals.push(me);
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openModals.at(-1) === me) close.current();
    };
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("keydown", keys);
      openModals.splice(openModals.indexOf(me), 1);
    };
  }, []);
  const box = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 wide:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role={role} aria-modal="true" aria-label={label} className={clsx("w-full border border-line bg-surface shadow-lift", MODAL_WIDTH[size], MODAL_SHAPE[size === "large" ? "large" : "box"])}>
        {children}
      </div>
    </div>
  );
  return createPortal(box, document.fullscreenElement ?? document.body);
}

/** The dialog: the message, for a prompt its field, and the buttons; Enter confirms. */
function DialogBox({ open }: { open: Open }) {
  const { t } = useI18n();
  const [value, setValue] = useState(open.value);
  const cancel = () => open.done(null);
  const accept = (e: React.FormEvent) => {
    e.preventDefault();
    open.done(open.kind === "prompt" ? value : "");
  };
  return (
    <Modal label={open.message} role={open.kind === "alert" ? "alertdialog" : "dialog"} onClose={cancel}>
      <form onSubmit={accept} className="space-y-4">
        <p className="text-[13.5px] leading-relaxed whitespace-pre-line text-ink">{open.message}</p>
        {open.kind === "prompt" && <input autoFocus onFocus={(e) => e.target.select()} value={value} onChange={(e) => setValue(e.target.value)} className="h-9 w-full rounded-md border border-line bg-surface-2 px-2 text-[13px] tabular" />}
        <div className="flex justify-end gap-2">
          {open.kind !== "alert" && <Button type="button" size="sm" onClick={cancel}>{t("action.cancel")}</Button>}
          <Button autoFocus={open.kind !== "prompt"} type="submit" size="sm" variant={open.danger ? "bad" : "primary"}>{t(open.kind === "alert" ? "action.ok" : "action.confirm")}</Button>
        </div>
      </form>
    </Modal>
  );
}
